import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { colorsFoundation } from "../../../theme/index.js";
import {
  familyOf,
  isKasko,
} from "../../../formdefs/claimWizard/familyMap.js";

const ACCENT = "#4A78BC";

// The dummy's 7 product-level Sparte buttons → a representative app `sparte`
// string (validated against Schaden::$sparte_docx_map on the backend).
const SPARTE_BUTTONS = [
  { label: "Wohngebäudeversicherung", sparte: "Gebäude (Feuer)" },
  { label: "Hausratversicherung", sparte: "Hausrat" },
  { label: "Privathaftpflicht", sparte: "Privathaftpflicht" },
  { label: "Kfz-Haftpflicht", sparte: "KFZ - Haftpflicht" },
  { label: "Kfz-Kasko", sparte: "KFZ - Vollkasko/Teilkasko" },
  { label: "Betriebsgebäudeversicherung", sparte: "Feuer" },
  { label: "Betriebshaftpflicht", sparte: "Betriebshaftpflicht" },
];

// Schadenart options per family (hardcoded to match the dummy), written to
// `schaden_ursache`. Haftpflicht has no fixed category (info note instead).
const KFZ_ARTS = ["Verkehrsunfall", "Parkschaden", "Wildschaden", "Sonstiges"];
const KFZ_KASKO_ARTS = [
  "Wildschaden",
  "Diebstahl / Raub",
  "Brand / Explosion",
  "Sturm / Hagel",
  "Glasschaden",
  "Marderbiss",
  "Vandalismus",
  "Verkehrsunfall",
];
const SACH_ARTS = [
  "Feuer",
  "Überspannung / Blitz",
  "Sturm / Hagel",
  "Leitungswasser",
  "Elementar",
];

const label = (text) => (
  <Typography
    sx={{
      fontSize: "12.5px",
      fontWeight: 600,
      color: colorsFoundation.gray[700],
      mb: "9px",
    }}
  >
    {text}
  </Typography>
);

// Segmented button row (matches the dummy's `seg`).
function Seg({ options, value, onPick, disabled }) {
  return (
    <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {options.map((o) => {
        const active = value === o;
        return (
          <Box
            key={o}
            component="button"
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onPick(o)}
            sx={{
              px: "15px",
              py: "9px",
              borderRadius: "9px",
              fontSize: "13.5px",
              fontWeight: active ? 600 : 500,
              fontFamily: "inherit",
              cursor: disabled ? "default" : "pointer",
              border: `1.5px solid ${active ? ACCENT : "#dde3ec"}`,
              backgroundColor: active ? "#EEF3FB" : "#fff",
              color: active ? "#3A6099" : "#6b7280",
            }}
          >
            {o}
          </Box>
        );
      })}
    </Box>
  );
}

Seg.propTypes = {
  options: PropTypes.array,
  value: PropTypes.string,
  onPick: PropTypes.func,
  disabled: PropTypes.bool,
};

/**
 * Broker Einordnung control — shows the customer's submitted summary
 * ("Kundenangaben"), a segmented Sparte selector (re-classification), and a
 * segmented Schadenart selector (writing `schaden_ursache`). Matches the Schmidt
 * click dummy.
 */
export default function ClaimEinordnung({
  currentSparte = "",
  values = {},
  onChange,
  onReclassify,
  disabled = false,
}) {
  const family = familyOf(currentSparte);
  const kasko = isKasko(currentSparte);
  const set = (name, value) => onChange?.({ target: { name, value } });

  // Haftpflicht has no fixed Schadenart — seed schaden_ursache so the required
  // check is satisfied and the broker can proceed.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || disabled) return;
    seeded.current = true;
    if (family === "haftpflicht" && !values.schaden_ursache) {
      set("schaden_ursache", "Haftpflichtschaden");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [family]);

  // Customer summary rows.
  const media = values.keine_medien
    ? "keine"
    : Array.isArray(values.sonstiges_unterlagen) && values.sonstiges_unterlagen.length
      ? `${values.sonstiges_unterlagen.length} Datei(en)`
      : "–";
  const schaetzung = values.schaden_hoehe_unbekannt
    ? "keine Angabe"
    : values.schaden_hoehe
      ? `${values.schaden_hoehe} €`
      : "–";
  const zeitpunkt = [values.schaden_datum, values.schaden_uhrzeit]
    .filter(Boolean)
    .join(", ");

  const rows = [
    [
      "Vertrag (Kundenangabe)",
      values.vertrag_unsicher
        ? "unsicher – bitte zuordnen"
        : values.vertragsnummer || currentSparte || "–",
    ],
    family === "kfz"
      ? [
          "Betroffenes Fahrzeug",
          [values.fahrzeug_kennzeichen, values.fahrzeug_modell]
            .filter(Boolean)
            .join(" · ") || "–",
        ]
      : null,
    ["Schadenort", values.schaden_ort || "–"],
    ["Schadenzeitpunkt", zeitpunkt || "–"],
    ["Beigefügte Medien", media],
    ["Schätzung des Kunden", schaetzung],
  ].filter(Boolean);

  const artOptions = family === "kfz" ? (kasko ? KFZ_KASKO_ARTS : KFZ_ARTS) : SACH_ARTS;

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Kundenangaben recap */}
      <Box>
        {label("Kundenangaben")}
        <Box
          sx={{
            border: `1px solid ${colorsFoundation.gray[200]}`,
            borderRadius: "12px",
            p: "6px 16px",
            backgroundColor: colorsFoundation.gray[50],
          }}
        >
          {rows.map(([l, v]) => (
            <Box
              key={l}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "20px",
                py: "7px",
                borderBottom: `1px solid ${colorsFoundation.gray[100]}`,
              }}
            >
              <Typography sx={{ fontSize: "12.5px", color: "#6b7280", flex: "none" }}>
                {l}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: colorsFoundation.gray[900],
                  textAlign: "right",
                }}
              >
                {v}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Betroffene Sparte — segmented, click re-classifies */}
      <Box>
        {label("Betroffene Sparte")}
        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {SPARTE_BUTTONS.map((b) => {
            const active = b.sparte === currentSparte;
            return (
              <Box
                key={b.label}
                component="button"
                type="button"
                disabled={disabled}
                onClick={(e) => !disabled && onReclassify?.(b.sparte, e)}
                sx={{
                  px: "15px",
                  py: "9px",
                  borderRadius: "9px",
                  fontSize: "13.5px",
                  fontWeight: active ? 600 : 500,
                  fontFamily: "inherit",
                  cursor: disabled ? "default" : "pointer",
                  border: `1.5px solid ${active ? ACCENT : "#dde3ec"}`,
                  backgroundColor: active ? "#EEF3FB" : "#fff",
                  color: active ? "#3A6099" : "#6b7280",
                }}
              >
                {b.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Schadenart — segmented per family, or a note for Haftpflicht */}
      {family === "haftpflicht" ? (
        <Box
          sx={{
            fontSize: "13px",
            color: "#6b7280",
            lineHeight: 1.5,
            backgroundColor: colorsFoundation.gray[50],
            border: `1px solid ${colorsFoundation.gray[200]}`,
            borderRadius: "9px",
            p: "11px 13px",
          }}
        >
          Haftpflichtschaden – keine feste Schadenursache-Kategorie. Bitte im
          Folgenden Anspruchsteller, Verschulden und Hergang erfassen.
        </Box>
      ) : (
        <Box>
          {label("Schadenart")}
          <Seg
            options={artOptions}
            value={values.schaden_ursache || ""}
            onPick={(v) => set("schaden_ursache", v)}
            disabled={disabled}
          />
        </Box>
      )}
    </Box>
  );
}

ClaimEinordnung.propTypes = {
  currentSparte: PropTypes.string,
  values: PropTypes.object,
  onChange: PropTypes.func,
  onReclassify: PropTypes.func,
  disabled: PropTypes.bool,
};
